import * as TonalInterval from '@tonaljs/interval';

import { ScaleDegreeInfo } from '../types';
import { INTERVAL_TO_ALTERATION } from './scale-constants/interval-alterations';

/**
 * Calculates scale degree information by comparing with the major scale
 * @param rootNote The root note of the scale
 * @param scaleType The type of scale
 * @param areNotesEnharmonic Function to check if two notes are enharmonic
 * @param getInterval Function to get interval between two notes
 * @returns Array of scale degree information objects
 */
export function calculateScaleDegrees(
  rootNote: string,
  _scaleType: string,
  areNotesEnharmonic: (noteA: string, noteB: string) => boolean,
  getInterval: (noteA: string, noteB: string) => string,
  scaleNotes: string[],
  majorScaleNotes: string[]
): ScaleDegreeInfo[] {
  // Map each note to its scale degree information
  return scaleNotes.map((note, index) => {
    // For root note, always return "1"
    if (index === 0 || areNotesEnharmonic(note, rootNote)) {
      return {
        degree: 1,
        alteration: '',
        display: '1',
        semitones: 0,
      };
    }

    // Calculate the scale degree (1-indexed)
    const scaleDegree = index + 1;

    // If the index is beyond the major scale length, use modulo arithmetic
    const majorIndex = index % majorScaleNotes.length;
    const majorScaleNote = majorScaleNotes[majorIndex];

    // Calculate semitones from root
    const interval = getInterval(rootNote, note);
    const semitones = TonalInterval.semitones(interval) || 0;

    if (!majorScaleNote) {
      return {
        degree: scaleDegree,
        alteration: '',
        display: String(scaleDegree),
        semitones,
      };
    }

    // Compare the current scale's note with the major scale's note
    const comparisonInterval = getInterval(majorScaleNote, note);

    // Default to unaltered degree
    let alteration = '';

    // If the interval isn't a perfect unison, apply the alteration
    if (comparisonInterval !== '1P') {
      alteration = INTERVAL_TO_ALTERATION[comparisonInterval] || '';
    }

    // Calculate the actual degree (considering the octave wrapping)
    const actualDegree = (majorIndex % 7) + 1;

    return {
      degree: actualDegree,
      alteration,
      display: `${alteration}${actualDegree}`,
      semitones,
    };
  });
}
